import { format } from 'date-fns';

import { userLocaleTimezone } from '@/lib/utils';

export type UnscoredType = 'preview' | 'maintenance' | string;

export interface UnscoredStatusDetails {
  unscoredType: UnscoredType;
  reason?: string;
  startDate?: string;
  endDate?: string;
  eventTime?: string;
}

export const getUnscoredStatusDetailsFromMetadata = (
  metadata: any,
): UnscoredStatusDetails | undefined => {
  if (!metadata) return undefined;

  const raw =
    metadata.unscoredStatusDetails ??
    metadata.UnscoredStatusDetails ??
    metadata.unscored_status_details ??
    metadata.Unscored_Status_Details ??
    metadata.unscored_scored_details ??
    metadata.Unscored_Scored_Details;

  if (!raw) {
    const maintenance = metadata.maintenance;
    if (maintenance && (maintenance.note || maintenance.eventTime)) {
      return {
        unscoredType: 'maintenance',
        reason: maintenance.note,
        eventTime: maintenance.eventTime,
      };
    }

    return undefined;
  }

  const unscoredType =
    raw.unscoredType ??
    raw.UnscoredType ??
    raw.unscored_type ??
    raw.Unscored_Type ??
    raw.unScoredType ??
    raw.type ??
    raw.Type ??
    '';
  const reason =
    raw.reason ?? raw.Reason ?? raw.note ?? raw.Note ?? raw.reason_text;
  const startDate =
    raw.startDate ?? raw.StartDate ?? raw.start_date ?? raw.Start_Date;
  const endDate = raw.endDate ?? raw.EndDate ?? raw.end_date ?? raw.End_Date;
  const eventTime =
    raw.eventTime ?? raw.EventTime ?? raw.event_time ?? raw.Event_Time;

  return {
    unscoredType,
    reason,
    startDate,
    endDate,
    eventTime,
  };
};

const DAY_MS = 24 * 60 * 60 * 1000;

export const formatEndDateWithTimezone = (endDateIso: string) => {
  return `${format(new Date(endDateIso), 'yyyy-MM-dd HH:mm')} ${userLocaleTimezone()}`;
};

export const getDaysLeftText = (endDateIso?: string) => {
  if (!endDateIso) return null;
  const endTime = new Date(endDateIso).getTime();
  if (Number.isNaN(endTime)) return null;

  const diffMs = endTime - Date.now();
  if (diffMs <= 0) return 'ended';

  const diffDays = diffMs / DAY_MS;
  if (diffDays < 1) return '<1 day left';

  const daysLeft = Math.ceil(diffDays);
  return `${daysLeft} day${daysLeft === 1 ? '' : 's'} left`;
};

export const normalizeUnscoredType = (value?: string) => {
  return (value ?? '').toString().trim().toLowerCase();
};
